import { FastField, Field } from 'formik';
import { type DateRange, type DateRangePickerProps as MuiDateRangePickerProps, DateRangeCalendar } from '@mui/x-date-pickers-pro';
import { makeStyles } from 'tss-react/mui';
import { FormHelperText, type TextFieldProps } from '@mui/material';
import { getCurrentDefaultZoneName, getDateTime } from '@sda/utils/date';
import { fieldToDateRangePicker } from './fieldToDateRangePicker';
import type { FieldProps } from 'formik';
import type { DateTime } from 'luxon';

const useStyles = makeStyles({ name: 'DateRangePicker' })((theme) => ({
  error: {
    '& > .MuiDateRangeCalendar-monthContainer': {
      borderColor: theme.palette.error.main,
    },
  },
  disabled: {
    opacity: 0.4,

    '& *': {
      pointerEvents: 'none',
    },
  },
}));

interface DateRangeProps extends Omit<MuiDateRangePickerProps<DateTime>, 'onChange' | 'value' | 'renderInput'> {
  readonly useFastField?: boolean;
  readonly timezone?: string;
  readonly id?: string;
  readonly name: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly value?: any;
  readonly status?: boolean;
  readonly fullWidth?: boolean;
  readonly helperText?: string;
  readonly InputProps?: TextFieldProps;
}

export const DateRangePicker: React.FC<DateRangeProps> = ({
  id, name, fullWidth, helperText: specifiedHelperText,
  timezone = null,
  useFastField = true,
  disabled = false,
  ...restProps
}) => {
  const { classes, cx } = useStyles();
  const FieldComponent = useFastField ? FastField : Field;

  return (
    <FieldComponent
      name={name}
    >
      {(props: FieldProps<DateRange<DateTime>>) => {
        const { value } = props.field;
        const { status: formStatus } = props.form;

        const error = props.form.errors && props.form.errors[name];
        const isTouched = props.form.touched && props.form.touched[name];

        const hasError = isTouched && !!error;

        let zone = getCurrentDefaultZoneName();

        if (value && value[0]) {
          zone = getDateTime(value[0]).zoneName;
        }

        if (formStatus && !!formStatus.timezone) {
          zone = formStatus.timezone;
        }

        const DatePickerProps = fieldToDateRangePicker({
          ...props,
          field: props.field,
        });

        return (
          <div>
            <DateRangeCalendar
              {...restProps}
              {...DatePickerProps}
              timezone={timezone || zone}
              calendars={1}
              className={cx(
                hasError && classes.error,
                disabled && classes.disabled,
              )}
            />

            {Boolean(isTouched) && Boolean(error) && (
              <FormHelperText error>
                {error as string}
              </FormHelperText>
            )}
          </div>
        );
      }}
    </FieldComponent>
  );
};
